import { Badge } from "@/design-system/components/ui";
import type { ClaimStatus, RevisionState } from "@/api/commissions";

const LABELS: Record<ClaimStatus, string> = {
  Draft: "Draft",
  Submitted: "Submitted",
  UnderReview: "Under Review",
  Contested: "Contested",
  Revised: "Revised",
  Withdrawn: "Withdrawn",
  Agreed: "Agreed",
  Payable: "Payable",
  PartiallyPaid: "Partially Paid",
  PaidInFull: "Paid in Full",
  Suggested: "Suggested",
};

/**
 * EVERY `ClaimStatus`, derived rather than retyped.
 *
 * 🔴 The point is that this CANNOT be a subset. `LABELS` above is a
 * `Record<ClaimStatus, string>`, so TypeScript refuses to compile it while a
 * union member is missing; reading its keys therefore yields all of them by
 * construction. A hand-written `ClaimStatus[]` gets no such help — a subset of
 * a union IS a valid array of it — which is exactly how the All Claims filter
 * came to offer ten of eleven statuses, silently, with `Suggested` (the state
 * every non-ADMIN claim is BORN in) the one it omitted.
 *
 * Order mirrors the C# enum's ordinals, whose own header says never to reorder
 * them, so the two lists read the same way top to bottom.
 *
 * It lives HERE and not beside `ClaimStatus` in `api/commissions.ts` for the
 * same reason `ESCALATION_ACTIONABLE_STATUSES` does: every test that mounts a
 * commissions page `vi.mock`s that module with an explicit factory, which
 * replaces the whole module, so a component importing a RUNTIME value from it
 * reads `undefined` under test.
 *
 * Deliberate SUBSETS of this list are legitimate and must stay subsets —
 * `ESCALATION_ACTIONABLE_STATUSES` below, and `TIMELINE_ORDER` on the claim
 * detail page. What must never be a subset is a list whose job is to offer the
 * reader every status there is.
 */
export const ALL_CLAIM_STATUSES: readonly ClaimStatus[] = Object.keys(
  LABELS,
) as ClaimStatus[];

type BadgeStatus = "draft" | "review" | "finalized" | "archived" | "error" | "info" | "gold";

const TO_VARIANT: Record<ClaimStatus, BadgeStatus> = {
  Draft: "draft",
  Submitted: "gold",
  UnderReview: "gold",
  Revised: "gold",
  Contested: "error",
  Withdrawn: "archived",
  Agreed: "info",
  Payable: "finalized",
  PartiallyPaid: "info",
  PaidInFull: "finalized",
  Suggested: "gold",
};

export function ClaimStatusBadge({ status }: { status: ClaimStatus }) {
  return <Badge status={TO_VARIANT[status]}>{LABELS[status]}</Badge>;
}

/**
 * Compound revision flag (PR-5 §5.4). Render alongside ClaimStatusBadge
 * when a claimant has unfinalized edits in flight on a Contested claim.
 * Gold pill differentiates from CONTESTED's red without alarming.
 */
export function RevisionStateBadge({ state }: { state: RevisionState }) {
  if (state !== "RevisionInProgress") return null;
  return <Badge status="gold">Revising</Badge>;
}

export function claimStatusLabel(status: ClaimStatus): string {
  return LABELS[status];
}

/** Mirrors `CommissionClaimService.EscalatableStatuses` — the statuses in which
 *  "put this in front of a manager" is a meaningful request, and therefore the
 *  only ones in which an escalation can be raised OR resolved.
 *
 *  🔴 Keep in lockstep with the server. `IsEscalated` has exactly one clearer, so
 *  a claim escalated legitimately and then withdrawn or settled keeps the flag for
 *  life — offering Resolve on one of those produces a 400 nobody can act on.
 *
 *  It lives HERE rather than beside `ClaimStatus` in `api/commissions.ts` on
 *  purpose: every test that mounts a commissions page `vi.mock`s that module with
 *  an explicit factory, which replaces the whole module — so a component importing
 *  a RUNTIME value from it reads `undefined` under test. That failure is invisible
 *  until a test happens to render a claim that reaches the expression. */
export const ESCALATION_ACTIONABLE_STATUSES: readonly ClaimStatus[] = [
  "Submitted",
  "UnderReview",
  "Contested",
  "Revised",
];

/** Mirrors the server's allowed-set in `CommissionClaimService.WithdrawAsync`
 *  — the statuses from which a claimant may withdraw (and, on a Suggested
 *  claim, DECLINE, which routes through the same terminal state).
 *
 *  🔴 Keep in lockstep with the server, and note which direction the drift ran:
 *  the client was NARROWER. This gate was written inline on the claim detail
 *  page as `Draft || Submitted || Contested || Revised`, omitting `Suggested`,
 *  while the server has always accepted it and `MyClaimsDashboard` has always
 *  called that very endpoint for a suggested claim ("Declined suggested
 *  claim"). Two surfaces, one endpoint, opposite answers — and the surface that
 *  refused was the one a reviewer reaches by clicking the claim.
 *
 *  A client narrower than its server withholds a working action and looks like
 *  a missing feature; a client WIDER than its server offers a button that 400s.
 *  Both are bad, so this is pinned as an equality, not an inclusion. */
export const WITHDRAWABLE_STATUSES: readonly ClaimStatus[] = [
  "Draft",
  "Submitted",
  "Contested",
  "Revised",
  "Suggested",
];
